import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './AgencyManagement.css';
import { getAgencies, getAgencyById, createAgency, updateAgency, deleteAgency } from '../../services/agenciesService';
import { getStates, getDistrictsByState } from '../../services/locationService';
import { getUsers } from '../../services/usersService';
import ExcelJS from 'exceljs';

const AgencyManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [agencyNameFilter, setAgencyNameFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [agencyNameDropdownOpen, setAgencyNameDropdownOpen] = useState(false);
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);
  const [agencyNameSearch, setAgencyNameSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [viewingAgency, setViewingAgency] = useState(null);
  const [formData, setFormData] = useState({
    agencyId: '',
    agencyName: '',
    agencyType: '',
    gstNumber: '',
    panNumber: '',
    addressLine1: '',
    addressLine2: '',
    pincode: '',
    country: 'India',
    state: '', // Will store state name for display
    stateId: null, // Will store state ID for API
    city: '', // Will store district name for display
    cityId: null, // Will store district ID for API
    latitude: '',
    longitude: '',
    contactPersonName: '',
    mobileNumber: '',
    alternateNumber: '',
    emailId: '',
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    upiId: '',
    username: '',
    password: '',
    status: 'Active'
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successAgencyName, setSuccessAgencyName] = useState('');
  const [successModalType, setSuccessModalType] = useState('add'); // 'add' or 'update'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [agencyToDelete, setAgencyToDelete] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deletedAgencyInfo, setDeletedAgencyInfo] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSampleCSVModal, setShowSampleCSVModal] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importValidAgencies, setImportValidAgencies] = useState([]);
  const [importInvalidRows, setImportInvalidRows] = useState([]);
  const [showImportSuccess, setShowImportSuccess] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState(0);
  const [importSkippedCount, setImportSkippedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const agencyNameDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const stateDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);
  // Form dropdown refs for searchable State and City
  const formStateDropdownRef = useRef(null);
  const formCityDropdownRef = useRef(null);
  // Form dropdown states for searchable State and City
  const [formStateDropdownOpen, setFormStateDropdownOpen] = useState(false);
  const [formCityDropdownOpen, setFormCityDropdownOpen] = useState(false);
  const [formStateSearch, setFormStateSearch] = useState('');
  const [formCitySearch, setFormCitySearch] = useState('');

  const [agencies, setAgencies] = useState([]);
  const [allAgenciesForFilters, setAllAgenciesForFilters] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  // Filter districts (separate from form districts)
  const [filterDistricts, setFilterDistricts] = useState([]);
  const [loadingFilterDistricts, setLoadingFilterDistricts] = useState(false);

  // Map API response (snake_case) to component format (camelCase)
  const mapAgencyFromAPI = (apiAgency) => {
    // Check if user data is included in the response (from JOIN)
    const userData = apiAgency.user || apiAgency.users || {};
    const passwordFromUser = userData.password || 
                            userData.password_hash || 
                            userData.passwordHash || 
                            '';
    
    return {
      id: apiAgency.id?.toString() || apiAgency.agency_id || '',
      agencyId: apiAgency.id?.toString() || apiAgency.agency_id || '',
      agencyName: apiAgency.agency_name || '',
      agencyType: apiAgency.agency_type || '',
      contactPerson: apiAgency.contact_person_name || '',
      contactPersonName: apiAgency.contact_person_name || '',
      mobileNumber: apiAgency.mobile_number || '',
      alternateNumber: apiAgency.alternate_number || '',
      emailId: apiAgency.email_id || '',
      addressLine1: apiAgency.address_line1 || '',
      addressLine2: apiAgency.address_line2 || '',
      city: apiAgency.district_name || apiAgency.city || '', // Use district_name from JOIN, fallback to city for backward compatibility
      cityId: apiAgency.city_id || apiAgency.district_id || null,
      state: apiAgency.state_name || apiAgency.state || '', // Use state_name from JOIN, fallback to state for backward compatibility
      stateId: apiAgency.state_id || null,
      country: apiAgency.country || 'India',
      pincode: apiAgency.pincode || '',
      gstNumber: apiAgency.gst_number || '',
      panNumber: apiAgency.pan_number || '',
      bankName: apiAgency.bank_name || '',
      accountHolderName: apiAgency.account_holder_name || '',
      accountNumber: apiAgency.account_number || '',
      ifscCode: apiAgency.ifsc_code || '',
      upiId: apiAgency.upi_id || '',
      username: apiAgency.username || userData.username || '',
      password: passwordFromUser || apiAgency.password || '',
      status: apiAgency.status || 'Active',
      latitude: apiAgency.latitude || '',
      longitude: apiAgency.longitude || '',
      createdAt: apiAgency.created_at,
      updatedAt: apiAgency.updated_at
    };
  };

  // Map component format (camelCase) to API format (snake_case)
  const mapAgencyToAPI = (agency) => {
    const payload = {
      agency_name: agency.agencyName,
      agency_type: agency.agencyType || null,
      contact_person_name: agency.contactPersonName || agency.contactPerson || null,
      mobile_number: agency.mobileNumber,
      alternate_number: agency.alternateNumber || null,
      email_id: agency.emailId || null,
      address_line1: agency.addressLine1 || null,
      address_line2: agency.addressLine2 || null,
      city: agency.city || '', // Send city name (not ID)
      pincode: agency.pincode || null,
      state: agency.state || '', // Send state name (not ID)
      country: agency.country || 'India',
      gst_number: agency.gstNumber || null,
      pan_number: agency.panNumber || null,
      bank_name: agency.bankName || null,
      account_holder_name: agency.accountHolderName || null,
      account_number: agency.accountNumber || null,
      ifsc_code: agency.ifscCode || null,
      upi_id: agency.upiId || null,
      latitude: agency.latitude !== '' && agency.latitude !== null && agency.longitude !== undefined ? Number(agency.latitude) : null,
      longitude: agency.longitude !== '' && agency.longitude !== null && agency.longitude !== undefined ? Number(agency.longitude) : null,
      status: agency.status || 'Active'
    };
    
    // Only include state_id if it's a valid number
    if (agency.stateId && Number(agency.stateId) > 0) {
      payload.state_id = Number(agency.stateId);
    }
    
    // Only include city_id if it's a valid number (backend requires > 0)
    if (agency.cityId && Number(agency.cityId) > 0) {
      payload.city_id = Number(agency.cityId);
    }
    
    console.log('mapAgencyToAPI payload:', payload);
    return payload;
  };

  // Fetch agencies from API
  const fetchAgencies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      if (agencyNameFilter) {
        params.agency_name = agencyNameFilter;
      }
      
      if (cityFilter) {
        params.city = cityFilter;
      }
      
      if (stateFilter) {
        params.state = stateFilter;
      }

      const response = await getAgencies(params);

      // Normalize response to support multiple API shapes
      const extractItems = (resp) => {
        if (!resp) return { items: [], pagination: {} };
        // If caller returned response.data (most services return response.data)
        const candidate = resp.data !== undefined ? resp : { data: resp };

        // candidate.data may be an array (flat), or an object with items/pagination
        if (Array.isArray(candidate.data)) {
          return { items: candidate.data, pagination: candidate.pagination || {} };
        }
        if (candidate.data && Array.isArray(candidate.data.items)) {
          return { items: candidate.data.items, pagination: candidate.data.pagination || candidate.pagination || {} };
        }
        if (Array.isArray(resp)) {
          return { items: resp, pagination: {} };
        }
        if (Array.isArray(resp.items)) {
          return { items: resp.items, pagination: resp.pagination || {} };
        }
        // Fallbacks
        return { items: candidate.data?.items || resp?.items || [], pagination: candidate.data?.pagination || resp?.pagination || {} };
      };

      const { items: agenciesData, pagination } = extractItems(response);
      const mappedAgencies = (Array.isArray(agenciesData) ? agenciesData : []).map(mapAgencyFromAPI);
      setAgencies(mappedAgencies);
      setTotalCount(pagination.total || mappedAgencies.length);
      
    } catch (err) {
      console.error('Error fetching agencies:', err);
      setError(err.message || 'Failed to fetch agencies. Please try again.');
      setAgencies([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, agencyNameFilter, cityFilter, stateFilter]);

  // Fetch agencies on mount and when pagination/filters change
  useEffect(() => {
    fetchAgencies();
  }, [fetchAgencies]);

  // Fetch unfiltered list to populate filter dropdowns (agency names, cities, states)
  const fetchAllForFilters = useCallback(async () => {
    try {
      // request a large limit to retrieve all possible values for filters
      const response = await getAgencies({ page: 1, limit: 1000 });
      // Reuse same extractor logic as fetchAgencies
      const extractItems = (resp) => {
        if (!resp) return [];
        const candidate = resp.data !== undefined ? resp : { data: resp };
        if (Array.isArray(candidate.data)) return candidate.data;
        if (candidate.data && Array.isArray(candidate.data.items)) return candidate.data.items;
        if (Array.isArray(resp)) return resp;
        if (Array.isArray(resp.items)) return resp.items;
        return candidate.data?.items || resp?.items || [];
      };
      const agenciesData = extractItems(response);
      setAllAgenciesForFilters((Array.isArray(agenciesData) ? agenciesData : []).map(mapAgencyFromAPI));
    } catch (err) {
      // Non-fatal - filters will simply be derived from current page
      console.warn('Failed to fetch full agency list for filters:', err);
    }
  }, []);

  useEffect(() => {
    fetchAllForFilters();
  }, [fetchAllForFilters]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [agencyNameFilter, cityFilter, stateFilter, itemsPerPage]);

  // Get unique values for filters
  // Use a separate unfiltered list to populate dropdown filter options so options don't disappear after filtering
  const uniqueAgencyNames = useMemo(() => Array.from(new Set(allAgenciesForFilters.map(a => a.agencyName))).filter(Boolean), [allAgenciesForFilters]);
  const uniqueCities = useMemo(() => Array.from(new Set(allAgenciesForFilters.map(a => a.city))).filter(Boolean), [allAgenciesForFilters]);
  const uniqueStates = useMemo(() => Array.from(new Set(allAgenciesForFilters.map(a => a.state))).filter(Boolean), [allAgenciesForFilters]);

  // Filter options based on search
  const filteredAgencyNames = useMemo(() => {
    if (!agencyNameSearch) return uniqueAgencyNames;
    return uniqueAgencyNames.filter(name => 
      name.toLowerCase().includes(agencyNameSearch.toLowerCase())
    );
  }, [uniqueAgencyNames, agencyNameSearch]);

  const filteredCities = useMemo(() => {
    if (!citySearch) return uniqueCities;
    return uniqueCities.filter(city => 
      city.toLowerCase().includes(citySearch.toLowerCase())
    );
  }, [uniqueCities, citySearch]);

  const filteredStates = useMemo(() => {
    if (!stateSearch) return uniqueStates;
    return uniqueStates.filter(state => 
      state.toLowerCase().includes(stateSearch.toLowerCase())
    );
  }, [uniqueStates, stateSearch]);

  // Note: Filtering is now handled server-side via API
  // The agencies array already contains filtered results from the API
  const filteredAgencies = agencies;

  // Pagination - use server-side total count
  const displayTotal = totalCount || agencies.length;
  const totalPages = Math.ceil(displayTotal / itemsPerPage) || 1;
  const pageStart = (currentPage - 1) * itemsPerPage;
  const current = agencies; // Already paginated by server

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (agencyNameDropdownRef.current && !agencyNameDropdownRef.current.contains(event.target)) {
        setAgencyNameDropdownOpen(false);
        setAgencyNameSearch('');
      }
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setCityDropdownOpen(false);
        setCitySearch('');
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setStateDropdownOpen(false);
        setStateSearch('');
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
      if (formStateDropdownRef.current && !formStateDropdownRef.current.contains(event.target)) {
        setFormStateDropdownOpen(false);
      }
      if (formCityDropdownRef.current && !formCityDropdownRef.current.contains(event.target)) {
        setFormCityDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [agencyNameFilter, cityFilter, stateFilter, searchQuery, itemsPerPage]);

  // Auto-hide success modals
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  useEffect(() => {
    if (showDeleteSuccess) {
      const timer = setTimeout(() => {
        setShowDeleteSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showDeleteSuccess]);

  useEffect(() => {
    if (showImportSuccess) {
      const timer = setTimeout(() => {
        setShowImportSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showImportSuccess]);

  const clearPaging = () => {
    setCurrentPage(1);
  };

  // Indian states and cities mapping (same as Customer Management)
  const indianStatesAndCities = {
    'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool', 'Rajahmundry', 'Tirupati', 'Kakinada', 'Kadapa', 'Anantapur', 'Eluru', 'Ongole', 'Nandyal', 'Machilipatnam', 'Adoni', 'Tenali', 'Chittoor', 'Hindupur', 'Proddatur', 'Bhimavaram'],
    'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Tawang', 'Bomdila', 'Pasighat', 'Tezu', 'Ziro', 'Along', 'Daporijo', 'Namsai', 'Roing', 'Khonsa', 'Aalo', 'Seppa', 'Yingkiong'],
    'Assam': ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Nagaon', 'Tinsukia', 'Tezpur', 'Bongaigaon', 'Dhubri', 'Diphu', 'North Lakhimpur', 'Karimganj', 'Goalpara', 'Barpeta', 'Sivasagar', 'Hailakandi', 'Golaghat', 'Dhekiajuli', 'Mangaldoi'],
    'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Purnia', 'Darbhanga', 'Arrah', 'Bihar Sharif', 'Katihar', 'Munger', 'Chapra', 'Sasaram', 'Hajipur', 'Sitamarhi', 'Samastipur', 'Bettiah', 'Motihari', 'Saharsa', 'Buxar', 'Kishanganj'],
    'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Durg', 'Korba', 'Rajpur', 'Raigarh', 'Jagdalpur', 'Ambikapur', 'Chirmiri', 'Dhamtari', 'Dantewada', 'Kanker', 'Kawardha', 'Mahasamund', 'Surajpur'],
    'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda', 'Bicholim', 'Valpoi', 'Curchorem', 'Quepem', 'Canacona'],
    'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar', 'Junagadh', 'Gandhidham', 'Anand', 'Navsari', 'Morbi', 'Nadiad', 'Surendranagar', 'Bharuch', 'Mehsana', 'Bhuj', 'Porbandar', 'Palanpur', 'Valsad'],
    'Haryana': ['Faridabad', 'Gurgaon', 'Panipat', 'Ambala', 'Yamunanagar', 'Rohtak', 'Hisar', 'Karnal', 'Sonipat', 'Panchkula', 'Sirsa', 'Bhiwani', 'Bahadurgarh', 'Jind', 'Thanesar', 'Kaithal', 'Rewari', 'Palwal', 'Fatehabad', 'Hansi'],
    'Himachal Pradesh': ['Shimla', 'Mandi', 'Solan', 'Dharamshala', 'Bilaspur', 'Kullu', 'Chamba', 'Hamirpur', 'Una', 'Kangra', 'Nahan', 'Palampur', 'Sundarnagar', 'Nurpur', 'Keylong'],
    'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro Steel City', 'Hazaribagh', 'Deoghar', 'Giridih', 'Adityapur', 'Ramgarh', 'Medininagar', 'Chirkunda', 'Phusro', 'Chaibasa', 'Gumla', 'Lohardaga', 'Dumka', 'Pakur', 'Godda'],
    'Karnataka': ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belagavi', 'Gulbarga', 'Davangere', 'Bellary', 'Bijapur', 'Shimoga', 'Tumkur', 'Raichur', 'Bidar', 'Hospet', 'Hassan', 'Udupi', 'Gadag', 'Chitradurga', 'Kolar', 'Mandya'],
    'Kerala': ['Kochi', 'Kozhikode', 'Thiruvananthapuram', 'Thrissur', 'Kollam', 'Kannur', 'Alappuzha', 'Kottayam', 'Palakkad', 'Manjeri', 'Thalassery', 'Ponnani', 'Kochi', 'Kayamkulam', 'Kottarakkara', 'Changanassery', 'Kasaragod', 'Pathanamthitta', 'Idukki', 'Wayanad'],
    'Madhya Pradesh': ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Ujjain', 'Sagar', 'Ratlam', 'Satna', 'Rewa', 'Murwara', 'Singrauli', 'Burhanpur', 'Khandwa', 'Morena', 'Bhind', 'Chhindwara', 'Vidisha', 'Guna', 'Shivpuri', 'Damoh'],
    'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kalyan', 'Vasai-Virar', 'Navi Mumbai', 'Amravati', 'Nanded', 'Kolhapur', 'Sangli', 'Jalgaon', 'Akola', 'Latur', 'Dhule', 'Ahmednagar', 'Ichalkaranji', 'Jalna', 'Bhusawal', 'Panvel', 'Satara', 'Beed', 'Yavatmal', 'Kamptee', 'Gondia', 'Barshi', 'Achalpur'],
    'Manipur': ['Imphal', 'Thoubal', 'Kakching', 'Ukhrul', 'Bishnupur', 'Churachandpur', 'Senapati', 'Tamenglong', 'Jiribam', 'Moreh', 'Lilong', 'Moirang', 'Mayang Imphal'],
    'Meghalaya': ['Shillong', 'Tura', 'Nongpoh', 'Jowai', 'Nongstoin', 'Baghmara', 'Williamnagar', 'Resubelpara', 'Mankachar', 'Ampati'],
    'Mizoram': ['Aizawl', 'Lunglei', 'Saiha', 'Champhai', 'Kolasib', 'Serchhip', 'Lawngtlai', 'Mamit', 'Khawzawl', 'Hnahthial'],
    'Nagaland': ['Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha', 'Zunheboto', 'Phek', 'Mon', 'Kiphire', 'Longleng', 'Peren'],
    'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur', 'Puri', 'Baleshwar', 'Bhadrak', 'Baripada', 'Balangir', 'Jharsuguda', 'Bargarh', 'Rayagada', 'Jeypore', 'Dhenkanal', 'Barbil', 'Kendujhar', 'Paradip', 'Bhuban', 'Talcher'],
    'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Pathankot', 'Hoshiarpur', 'Batala', 'Moga', 'Abohar', 'Khanna', 'Phagwara', 'Muktsar', 'Barnala', 'Firozpur', 'Kapurthala', 'Sultanpur Lodhi', 'Fazilka', 'Gurdaspur', 'Malerkotla'],
    'Rajasthan': ['Jaipur', 'Jodhpur', 'Kota', 'Bikaner', 'Ajmer', 'Udaipur', 'Bhilwara', 'Alwar', 'Bharatpur', 'Sri Ganganagar', 'Sikar', 'Pali', 'Tonk', 'Chittorgarh', 'Hanumangarh', 'Beawar', 'Karauli', 'Dholpur', 'Bundi', 'Baran', 'Churu', 'Jhunjhunu', 'Nagaur', 'Jhalawar', 'Banswara'],
    'Sikkim': ['Gangtok', 'Namchi', 'Mangan', 'Gyalshing', 'Singtam', 'Rangpo', 'Jorethang', 'Pakyong', 'Rhenock', 'Soreng'],
    'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Tiruppur', 'Erode', 'Vellore', 'Thoothukudi', 'Dindigul', 'Thanjavur', 'Hosur', 'Nagercoil', 'Kanchipuram', 'Karaikudi', 'Neyveli', 'Cuddalore', 'Kumbakonam', 'Tiruvannamalai', 'Pollachi', 'Rajapalayam', 'Gudiyatham', 'Pudukkottai', 'Ambur'],
    'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Ramagundam', 'Khammam', 'Mahbubnagar', 'Nalgonda', 'Adilabad', 'Suryapet', 'Miryalaguda', 'Siddipet', 'Jagtial', 'Mancherial', 'Kamareddy', 'Kothagudem', 'Bhongir', 'Bodhan', 'Narayanpet', 'Wanaparthy'],
    'Tripura': ['Agartala', 'Udaipur', 'Dharmanagar', 'Kailashahar', 'Belonia', 'Khowai', 'Ambassa', 'Kumarghat', 'Sabroom', 'Teliamura'],
    'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Ghaziabad', 'Agra', 'Meerut', 'Varanasi', 'Allahabad', 'Bareilly', 'Aligarh', 'Moradabad', 'Saharanpur', 'Gorakhpur', 'Noida', 'Firozabad', 'Jhansi', 'Muzaffarnagar', 'Mathura', 'Rampur', 'Shahjahanpur', 'Farrukhabad', 'Fatehpur', 'Budaun', 'Sitapur', 'Hapur', 'Etawah', 'Unnao', 'Pilibhit', 'Lakhimpur', 'Basti', 'Deoria'],
    'Uttarakhand': ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani', 'Rudrapur', 'Kashipur', 'Rishikesh', 'Nainital', 'Mussoorie', 'Pithoragarh', 'Almora', 'Srinagar', 'Kotdwara', 'Ramnagar', 'Pauri', 'Chamoli Gopeshwar', 'New Tehri', 'Uttarkashi'],
    'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Bardhaman', 'Malda', 'Baharampur', 'Habra', 'Kharagpur', 'Shantipur', 'Ranaghat', 'Krishnanagar', 'Nabadwip', 'Medinipur', 'Chandannagar', 'Jalpaiguri', 'Baidyabati', 'Darjeeling', 'Purulia', 'Bankura', 'Alipurduar', 'Raiganj'],
    'Delhi': ['New Delhi', 'Delhi Cantonment', 'Narela', 'Najafgarh', 'Rohini', 'Dwarka', 'Patparganj'],
    'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla', 'Sopore', 'Kathua', 'Udhampur', 'Poonch', 'Rajouri', 'Kupwara', 'Bandipora', 'Ganderbal', 'Pulwama', 'Shopian', 'Budgam', 'Kulgam', 'Doda', 'Ramban', 'Kishtwar', 'Reasi'],
    'Ladakh': ['Leh', 'Kargil', 'Drass', 'Nubra Valley', 'Zanskar'],
    'Puducherry': ['Puducherry', 'Karaikal', 'Mahe', 'Yanam'],
    'Andaman and Nicobar Islands': ['Port Blair', 'Diglipur', 'Mayabunder', 'Rangat', 'Car Nicobar'],
    'Chandigarh': ['Chandigarh'],
    'Dadra and Nagar Haveli and Daman and Diu': ['Silvassa', 'Daman', 'Diu'],
    'Lakshadweep': ['Kavaratti', 'Agatti', 'Amini', 'Andrott', 'Kadmat', 'Kalpeni', 'Kiltan', 'Minicoy']
  };

  // Fetch states on component mount
  useEffect(() => {
    const loadStates = async () => {
      try {
        setLoadingStates(true);
        const response = await getStates();
        console.log('Agency - States API response:', response);
        
        // Try multiple response formats
        let statesData = [];
        if (Array.isArray(response)) {
          statesData = response;
        } else if (Array.isArray(response?.data)) {
          statesData = response.data;
        } else if (Array.isArray(response?.items)) {
          statesData = response.items;
        } else if (Array.isArray(response?.data?.data)) {
          statesData = response.data.data;
        } else if (Array.isArray(response?.data?.items)) {
          statesData = response.data.items;
        } else if (response?.success && Array.isArray(response?.data)) {
          statesData = response.data;
        }
        
        console.log('Agency - Parsed states data:', statesData);
        setStates(statesData);
      } catch (err) {
        console.error('Agency - Error fetching states:', err);
        setStates([]);
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, []);

  // Fetch districts when state changes in form
  useEffect(() => {
    const loadDistricts = async () => {
      console.log('Agency - loadDistricts triggered. formData.state:', formData.state, 'states.length:', states.length);
      
      if (!formData.state) {
        console.log('Agency - No state selected, clearing districts');
        setDistricts([]);
        return;
      }
      
      if (states.length === 0) {
        console.log('Agency - States array is empty, cannot find state ID');
        setDistricts([]);
        return;
      }
      
      try {
        // Find state ID from state name
        const selectedState = states.find(s => {
          const stateName = (s.name || s.state_name || s.state || '').toLowerCase().trim();
          const formStateName = formData.state.toLowerCase().trim();
          return stateName === formStateName;
        });
        
        if (!selectedState) {
          console.error('Agency - State not found in states array. Selected:', formData.state);
          setDistricts([]);
          return;
        }
        
        const stateId = selectedState.id || selectedState.state_id;
        if (!stateId) {
          console.error('Agency - State ID not found in selected state object:', selectedState);
          setDistricts([]);
          return;
        }
        
        console.log('Agency - Calling districts API with state_id:', stateId);
        setLoadingDistricts(true);
        const response = await getDistrictsByState(stateId);
        console.log('Agency - Districts API response for state', formData.state, ':', response);
        
        // Try multiple response formats
        let districtsData = [];
        if (Array.isArray(response)) {
          districtsData = response;
        } else if (Array.isArray(response?.data)) {
          districtsData = response.data;
        } else if (Array.isArray(response?.items)) {
          districtsData = response.items;
        } else if (Array.isArray(response?.data?.data)) {
          districtsData = response.data.data;
        } else if (Array.isArray(response?.data?.items)) {
          districtsData = response.data.items;
        } else if (response?.success && Array.isArray(response?.data)) {
          districtsData = response.data;
        }
        
        setDistricts(districtsData);
        console.log('Agency - Loaded', districtsData.length, 'districts for state', formData.state);
      } catch (err) {
        console.error('Agency - Error loading districts for state', formData.state, ':', err);
        setDistricts([]);
      } finally {
        setLoadingDistricts(false);
      }
    };
    
    loadDistricts();
  }, [formData.state, states]);

  // Load districts for filter when state filter changes
  useEffect(() => {
    if (!stateFilter || !states.length) {
      setFilterDistricts([]);
      return;
    }

    const loadFilterDistricts = async () => {
      // Find the selected state from states array
      const selectedState = states.find(s => {
        const stateName = (s.name || s.state_name || s.state || '').trim();
        return stateName === stateFilter;
      });

      if (!selectedState) {
        setFilterDistricts([]);
        return;
      }

      const stateId = selectedState.id || selectedState.state_id;
      if (!stateId) {
        setFilterDistricts([]);
        return;
      }

      try {
        setLoadingFilterDistricts(true);
        const response = await getDistrictsByState(stateId);
        console.log('Agency Filter - Districts API response for state', stateFilter, ':', response);

        // Try multiple response formats
        let districtsData = [];
        if (Array.isArray(response)) {
          districtsData = response;
        } else if (Array.isArray(response?.data)) {
          districtsData = response.data;
        } else if (Array.isArray(response?.items)) {
          districtsData = response.items;
        } else if (Array.isArray(response?.data?.data)) {
          districtsData = response.data.data;
        } else if (Array.isArray(response?.data?.items)) {
          districtsData = response.data.items;
        } else if (response?.success && Array.isArray(response?.data)) {
          districtsData = response.data;
        }

        setFilterDistricts(districtsData);
        console.log('Agency Filter - Loaded', districtsData.length, 'districts for state', stateFilter);
      } catch (err) {
        console.error('Agency Filter - Error loading districts for state', stateFilter, ':', err);
        setFilterDistricts([]);
      } finally {
        setLoadingFilterDistricts(false);
      }
    };

    loadFilterDistricts();
  }, [stateFilter, states]);

  // Get districts (cities) for selected state - now from API
  const getCitiesForState = (stateName) => {
    if (!stateName || districts.length === 0) {
      return [];
    }
    // Districts are already filtered by state from the API call
    const districtNames = districts
      .map(d => d.name || d.district_name || d.district || '')
      .filter(Boolean)
      .sort();
    return districtNames;
  };

  // Filtered states for form dropdown
  const filteredFormStates = useMemo(() => {
    if (!formStateSearch) return states;
    const searchLower = formStateSearch.toLowerCase();
    return states.filter(s => {
      const stateName = (s.name || s.state_name || s.state || '').toLowerCase();
      return stateName.includes(searchLower);
    });
  }, [states, formStateSearch]);

  // Filtered cities for form dropdown
  const filteredFormCities = useMemo(() => {
    const cities = getCitiesForState(formData.state);
    if (!formCitySearch) return cities;
    const searchLower = formCitySearch.toLowerCase();
    return cities.filter(city => city.toLowerCase().includes(searchLower));
  }, [formData.state, districts, formCitySearch]);

  // Filtered states for filter dropdown (from API)
  const filteredFilterStates = useMemo(() => {
    if (!stateSearch) return states;
    const searchLower = stateSearch.toLowerCase();
    return states.filter(s => {
      const stateName = (s.name || s.state_name || s.state || '').toLowerCase();
      return stateName.includes(searchLower);
    });
  }, [states, stateSearch]);

  // Filtered cities/districts for filter dropdown (from API, based on selected state)
  const filteredFilterCities = useMemo(() => {
    if (!citySearch) return filterDistricts;
    const searchLower = citySearch.toLowerCase();
    return filterDistricts.filter(d => {
      const districtName = (d.name || d.district_name || d.district || '').toLowerCase();
      return districtName.includes(searchLower);
    });
  }, [filterDistricts, citySearch]);

  // Generate auto Agency ID
  const generateAgencyId = () => {
    const existingIds = agencies.map(a => {
      const match = a.id.match(/AG(\d+)/);
      return match ? parseInt(match[1]) : 0;
    });
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
    const nextId = maxId + 1;
    return `AG${String(nextId).padStart(3, '0')}`;
  };

  const handleAddAgency = () => {
    const newId = generateAgencyId();
    setFormData({
      agencyId: newId,
      agencyName: '',
      agencyType: '',
      gstNumber: '',
      panNumber: '',
      addressLine1: '',
      addressLine2: '',
      pincode: '',
      country: 'India',
      state: '',
      stateId: null,
      city: '',
      cityId: null,
      latitude: '',
      longitude: '',
      contactPersonName: '',
      mobileNumber: '',
      alternateNumber: '',
      emailId: '',
      bankName: '',
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      upiId: '',
      username: '',
      password: '',
      status: 'Active'
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingAgency(null);
    setFormData({
      agencyId: '',
      agencyName: '',
      agencyType: '',
      gstNumber: '',
      panNumber: '',
      addressLine1: '',
      addressLine2: '',
      pincode: '',
      country: 'India',
      state: '',
      stateId: null,
      city: '',
      cityId: null,
      latitude: '',
      longitude: '',
      contactPersonName: '',
      mobileNumber: '',
      alternateNumber: '',
      emailId: '',
      bankName: '',
      accountHolderName: '',
      accountNumber: '',
      ifscCode: '',
      upiId: '',
      username: '',
      password: '',
      status: 'Active'
    });
    setFormErrors({});
    setShowPassword(false);
    setFormStateSearch('');
    setFormCitySearch('');
    setFormStateDropdownOpen(false);
    setFormCityDropdownOpen(false);
  };

  const handleReset = async () => {
    if (showEditModal && editingAgency) {
      // Reset to original agency data (will be handled by handleEditClick logic)
      await handleEditClick(editingAgency);
    } else {
      // Reset to empty form for add
      const newId = generateAgencyId();
      setFormData({
        agencyId: newId,
        agencyName: '',
        agencyType: '',
        gstNumber: '',
        panNumber: '',
        addressLine1: '',
        addressLine2: '',
        pincode: '',
        country: 'India',
        state: '',
        stateId: null,
        city: '',
        cityId: null,
        latitude: '',
        longitude: '',
        contactPersonName: '',
        mobileNumber: '',
        alternateNumber: '',
        emailId: '',
        bankName: '',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        upiId: '',
        username: '',
        password: '',
        status: 'Active'
      });
    }
    setFormErrors({});
    setShowPassword(false);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.agencyName.trim()) {
      errors.agencyName = 'Transporter Name is required';
    }
    
    if (!formData.mobileNumber.trim()) {
      errors.mobileNumber = 'Mobile Number is required';
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber.trim())) {
      errors.mobileNumber = 'Mobile Number must be 10 digits';
    }

    if (formData.emailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId.trim())) {
      errors.emailId = 'Please enter a valid email address';
    } else if (formData.emailId && formData.emailId.trim()) {
      // Check for duplicate email (case-insensitive)
      const emailToCheck = formData.emailId.trim().toLowerCase();
      const existingAgency = allAgenciesForFilters.find(agency => {
        // Exclude current agency when editing
        if (showEditModal && editingAgency && (agency.id === editingAgency.id || agency.id === formData.agencyId)) {
          return false;
        }
        const agencyEmail = (agency.emailId || '').toLowerCase();
        return agencyEmail === emailToCheck && agencyEmail !== '';
      });
      
      if (existingAgency) {
        errors.emailId = 'This email address is already in use by another transporter';
      }
    }
    
    // GST validation (if provided) - Indian GSTIN format
    if (formData.gstNumber && formData.gstNumber.trim()) {
      const gst = formData.gstNumber.trim().toUpperCase();
      const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstRegex.test(gst)) {
        errors.gstNumber = 'Please enter a valid GSTIN (15 characters)';
      }
    }

    // PAN validation (if provided)
    if (formData.panNumber && formData.panNumber.trim()) {
      const pan = formData.panNumber.trim().toUpperCase();
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (!panRegex.test(pan)) {
        errors.panNumber = 'Please enter a valid PAN (e.g. AAAAA9999A)';
      }
    }

    // UPI ID validation (if provided) - basic email-like pattern
    if (formData.upiId && formData.upiId.trim()) {
      const upi = formData.upiId.trim();
      const upiRegex = /^[^\s@]+@[^\s@]+$/;
      if (!upiRegex.test(upi)) {
        errors.upiId = 'Please enter a valid UPI ID (e.g. name@bank)';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate latitude/longitude when provided
  const validateLatLong = () => {
    const errors = {};
    const latRaw = formData.latitude;
    const lngRaw = formData.longitude;
    if (latRaw !== '' && latRaw !== null && latRaw !== undefined) {
      const lat = Number(latRaw);
      if (Number.isNaN(lat) || lat < -90 || lat > 90) {
        errors.latitude = 'Latitude must be a number between -90 and 90';
      }
    }
    if (lngRaw !== '' && lngRaw !== null && lngRaw !== undefined) {
      const lng = Number(lngRaw);
      if (Number.isNaN(lng) || lng < -180 || lng > 180) {
        errors.longitude = 'Longitude must be a number between -180 and 180';
      }
    }
    // merge into existing formErrors for display
    if (Object.keys(errors).length > 0) {
      setFormErrors(prev => ({ ...prev, ...errors }));
      return false;
    }
    return true;
  };

  const handleViewClick = (agency) => {
    setViewingAgency(agency);
    setShowViewModal(true);
  };

  const handleDeleteClick = (agency) => {
    setAgencyToDelete(agency);
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setAgencyToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (!agencyToDelete) return;
    const performDelete = async () => {
      setLoading(true);
      try {
        // Call API to delete agency
        await deleteAgency(agencyToDelete.id);

        // Update UI
        setDeletedAgencyInfo({ agencyName: agencyToDelete.agencyName });
        setShowDeleteConfirm(false);
        setAgencyToDelete(null);
        setShowDeleteSuccess(true);

        // Refresh list from server to reflect deletion (and pagination)
        await fetchAgencies();
      } catch (err) {
        console.error('Error deleting agency:', err);
        setError(err.message || 'Failed to delete agency. Please try again.');
        // keep the confirm modal open so user can retry or cancel
      } finally {
        setLoading(false);
      }
    };

    performDelete();
  };

  const handleDeleteSuccessClose = () => {
    setShowDeleteSuccess(false);
    setDeletedAgencyInfo(null);
  };

  const handleExport = async () => {
    try {
      const headers = [
        'Transporter ID',
        'Transporter Name',
        'Transporter Type',
        'GST Number',
        'PAN Number',
        'Address Line 1',
        'Address Line 2',
        'Pincode',
        'Country',
        'State',
        'City',
        'Contact Person Name',
        'Mobile Number',
        'Alternate Number',
        'Email ID',
        'Bank Name',
        'Account Holder Name',
        'Account Number',
        'IFSC Code',
        'UPI Id',
        'Username',
        'Password',
        'Status'
      ];

      // Create workbook and worksheet using ExcelJS
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Agencies Export');

      // Add header row
      worksheet.addRow(headers);

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

      // Add data rows
      agencies.forEach(a => {
        worksheet.addRow([
          a.id || '',
          a.agencyName || '',
          a.agencyType || '',
          a.gstNumber || '',
          a.panNumber || '',
          a.addressLine1 || '',
          a.addressLine2 || '',
          a.pincode || '',
          a.country || 'India',
          a.state || '',
          a.city || '',
          a.contactPerson || '',
          a.mobileNumber || '',
          a.alternateNumber || '',
          a.emailId || '',
          a.bankName || '',
          a.accountHolderName || '',
          a.accountNumber || '',
          a.ifscCode || '',
          a.upiId || '',
          a.username || '',
          a.password || '',
          a.status || 'Active'
        ]);
      });

      // Set column widths to ensure headers are fully visible
      worksheet.getColumn(1).width = 18;  // Transporter ID
      worksheet.getColumn(2).width = 25;  // Transporter Name
      worksheet.getColumn(3).width = 20;  // Transporter Type
      worksheet.getColumn(4).width = 18;  // GST Number
      worksheet.getColumn(5).width = 18;  // PAN Number
      worksheet.getColumn(6).width = 30;  // Address Line 1
      worksheet.getColumn(7).width = 30;  // Address Line 2
      worksheet.getColumn(8).width = 12;  // Pincode
      worksheet.getColumn(9).width = 15;  // Country
      worksheet.getColumn(10).width = 20; // State
      worksheet.getColumn(11).width = 20; // City
      worksheet.getColumn(12).width = 25; // Contact Person Name
      worksheet.getColumn(13).width = 18; // Mobile Number
      worksheet.getColumn(14).width = 20; // Alternate Number
      worksheet.getColumn(15).width = 30; // Email ID
      worksheet.getColumn(16).width = 25; // Bank Name
      worksheet.getColumn(17).width = 25; // Account Holder Name
      worksheet.getColumn(18).width = 20; // Account Number
      worksheet.getColumn(19).width = 15; // IFSC Code
      worksheet.getColumn(20).width = 20; // UPI Id
      worksheet.getColumn(21).width = 20; // Username
      worksheet.getColumn(22).width = 20; // Password
      worksheet.getColumn(23).width = 15; // Status

      // Generate Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Agencies_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting agencies:', error);
      alert('Failed to export agencies. Please try again.');
    }
  };

  // Parse CSV text into array of objects using headers
  const parseCSV = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.length === 0) return [];
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').replace(/""/g, '"'));
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      for (let j = 0; j < lines[i].length; j++) {
        const char = lines[i][j];
        if (char === '"') {
          if (inQuotes && lines[i][j + 1] === '"') {
            current += '"';
            j++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      const obj = {};
      headers.forEach((h, idx) => {
        obj[h] = (values[idx] || '').replace(/^"|"$/g, '').replace(/""/g, '"');
      });
      rows.push(obj);
    }
    return rows;
  };

  const mapRowToAgency = (row) => {
    const agencyId = (row['Transporter ID'] || '').trim() || generateAgencyId();
    return {
      id: agencyId,
      agencyName: (row['Transporter Name'] || '').trim(),
      agencyType: (row['Transporter Type'] || '').trim(),
      gstNumber: (row['GST Number'] || '').trim(),
      panNumber: (row['PAN Number'] || '').trim(),
      addressLine1: (row['Address Line 1'] || '').trim(),
      addressLine2: (row['Address Line 2'] || '').trim(),
      pincode: (row['Pincode'] || '').trim(),
      country: (row['Country'] || 'India').trim(),
      state: (row['State'] || '').trim(),
      city: (row['City'] || '').trim(),
      contactPerson: (row['Contact Person Name'] || '').trim(),
      mobileNumber: (row['Mobile Number'] || '').trim(),
      alternateNumber: (row['Alternate Number'] || '').trim(),
      emailId: (row['Email ID'] || '').trim(),
      bankName: (row['Bank Name'] || '').trim(),
      accountHolderName: (row['Account Holder Name'] || '').trim(),
      accountNumber: (row['Account Number'] || '').trim(),
      ifscCode: (row['IFSC Code'] || '').trim(),
      upiId: (row['UPI Id'] || '').trim(),
      username: (row['Username'] || '').trim(),
      password: (row['Password'] || '').trim(),
      latitude: (row['Latitude'] || '').trim(),
      longitude: (row['Longitude'] || '').trim(),
      status: (row['Status'] || 'Active').trim()
    };
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = reader.result;
        const rows = parseCSV(text);
        // Filter out empty rows (missing mandatory fields like Transporter Name)
        const nonEmptyRows = rows.filter(r => (r['Transporter Name'] || '').trim() !== '');
        const valid = [];
        const invalid = [];
        const existingNames = new Set(agencies.map(a => a.agencyName.toLowerCase()));
        const existingIds = new Set(agencies.map(a => a.id));
        
        nonEmptyRows.forEach((row, idx) => {
          const agencyName = (row['Transporter Name'] || '').trim();
          const agencyId = (row['Transporter ID'] || '').trim();
          let reason = '';
          
          if (!agencyName) {
            reason = 'Transporter Name is required';
          } else if (existingNames.has(agencyName.toLowerCase())) {
            reason = 'Transporter Name already exists';
          } else if (agencyId && existingIds.has(agencyId)) {
            reason = 'Transporter ID already exists';
          }
          
          if (reason) {
            invalid.push({ row: idx + 2, agencyName: agencyName || 'N/A', reason });
          } else {
            const agency = mapRowToAgency(row);
            valid.push(agency);
            if (agencyName) existingNames.add(agencyName.toLowerCase());
            if (agency.id) existingIds.add(agency.id);
          }
        });
        
        setImportFileName(file.name);
        setImportValidAgencies(valid);
        setImportInvalidRows(invalid);
        setShowImportModal(true);
        setShowSampleCSVModal(false);
      } catch (error) {
        alert('Error reading CSV file: ' + error.message);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImportConfirm = () => {
    setAgencies(prev => [...importValidAgencies, ...prev]);
    setImportSuccessCount(importValidAgencies.length);
    setImportSkippedCount(importInvalidRows.length);
    setShowImportModal(false);
    setImportValidAgencies([]);
    setImportInvalidRows([]);
    setImportFileName('');
    setShowImportSuccess(true);
  };

  const handleImportCancel = () => {
    setShowImportModal(false);
    setImportValidAgencies([]);
    setImportInvalidRows([]);
    setImportFileName('');
  };

  const handleDownloadSampleCSV = () => {
    const headers = [
      'Transporter Name',
      'Transporter Type',
      'GST Number',
      'PAN Number',
      'Address Line 1',
      'Address Line 2',
      'Pincode',
      'Country',
      'State',
      'City',
      'Contact Person Name',
      'Mobile Number',
      'Alternate Number',
      'Email ID',
      'Bank Name',
      'Account Holder Name',
      'Account Number',
      'IFSC Code',
      'UPI Id',
      'Username',
      'Password',
      'Status'
    ];
    const escapeCSV = (str) => {
      if (str === null || str === undefined) return '';
      const s = String(str);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };
    const sampleRow = [
      'Sample Agency',
      'Delivery',
      '33AABCS1234F1ZC',
      'AABCS1234F',
      'No. 22, Main Road',
      'Near SBI Bank',
      '600001',
      'India',
      'Tamil Nadu',
      'Chennai',
      'Ramesh Kumar',
      '9876543210',
      '9876500000',
      'sample@transporter.com',
      'HDFC Bank',
      'Sample Transporter',
      '123456789012',
      'HDFC0001234',
      'sample@hdfc',
      'sample_agency',
      'password123',
      '12.9716',
      '77.5946',
      'Active'
    ];
    const csv = '\ufeff' + [headers.map(escapeCSV).join(','), sampleRow.map(escapeCSV).join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Transporter_Import_Sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportClick = (e) => {
    e.preventDefault();
    setShowSampleCSVModal(true);
  };

  const handleProceedWithImport = () => {
    setShowSampleCSVModal(false);
    document.getElementById('agency-import-file-input').click();
  };

  const handleEditClick = async (agency) => {
    try {
      setLoading(true);
      // Fetch full agency details including password
      const agencyId = agency.id || agency.agency_id;
      let fullAgencyData = agency;
      let userPassword = '';
      
      if (agencyId) {
        try {
          const response = await getAgencyById(agencyId);
          if (response?.success && response?.data) {
            fullAgencyData = mapAgencyFromAPI(response.data);
          } else if (response?.data) {
            fullAgencyData = mapAgencyFromAPI(response.data);
          }
        } catch (err) {
          console.error('Error fetching agency details:', err);
          // Continue with existing agency data if fetch fails
        }
      }
      
      // Fetch user data to get password from users table
      try {
        const emailId = fullAgencyData.emailId || agency.emailId || '';
        if (emailId || agencyId) {
          // Try to fetch user by email or agency_id
          const userParams = {};
          if (emailId) {
            userParams.search = emailId;
          }
          
          const usersResponse = await getUsers({ ...userParams, limit: 100 });
          let usersData = [];
          
          if (usersResponse?.success && Array.isArray(usersResponse?.data)) {
            usersData = usersResponse.data;
          } else if (Array.isArray(usersResponse?.data?.data)) {
            usersData = usersResponse.data.data;
          } else if (Array.isArray(usersResponse?.data)) {
            usersData = usersResponse.data;
          } else if (Array.isArray(usersResponse)) {
            usersData = usersResponse;
          }
          
          // Find the user associated with this agency
          const associatedUser = usersData.find(user => {
            const userAgencyId = user.agency_id || user.agencyId;
            const userEmail = (user.email || '').toLowerCase();
            const agencyEmail = (emailId || '').toLowerCase();
            
            return (userAgencyId && userAgencyId.toString() === agencyId.toString()) ||
                   (userEmail && agencyEmail && userEmail === agencyEmail);
          });
          
          if (associatedUser) {
            // Get password from user - check various possible field names
            userPassword = associatedUser.password || 
                          associatedUser.password_hash || 
                          associatedUser.passwordHash || 
                          '';
          }
        }
      } catch (err) {
        console.error('Error fetching user data for password:', err);
        // Continue without password if fetch fails
      }
      
      setEditingAgency(fullAgencyData);
      
      // Populate formData with stateId and cityId if available
      let stateId = fullAgencyData.stateId || null;
      let cityId = fullAgencyData.cityId || null;
      let stateName = fullAgencyData.state || '';
      let cityName = fullAgencyData.city || '';
      
      // If IDs are not available, try to look them up from names
      if (!stateId && stateName && states.length > 0) {
        const matchedState = states.find(s => {
          const sName = (s.name || s.state_name || s.state || '').trim();
          return sName.toLowerCase() === stateName.toLowerCase();
        });
        if (matchedState) {
          stateId = matchedState.id || matchedState.state_id;
          stateName = matchedState.name || matchedState.state_name || matchedState.state || stateName;
        }
      }
      
      // Load districts for the selected state
      if (stateId) {
        try {
          setLoadingDistricts(true);
          const response = await getDistrictsByState(stateId);
          let districtsData = [];
          if (Array.isArray(response)) {
            districtsData = response;
          } else if (Array.isArray(response?.data)) {
            districtsData = response.data;
          } else if (Array.isArray(response?.items)) {
            districtsData = response.items;
          } else if (Array.isArray(response?.data?.data)) {
            districtsData = response.data.data;
          } else if (response?.success && Array.isArray(response?.data)) {
            districtsData = response.data;
          }
          setDistricts(districtsData);
          
          // If cityId is not available, try to find it from city name
          if (!cityId && cityName && districtsData.length > 0) {
            const matchedDistrict = districtsData.find(d => {
              const dName = (d.name || d.district_name || d.district || '').trim();
              return dName.toLowerCase() === cityName.toLowerCase();
            });
            if (matchedDistrict) {
              cityId = matchedDistrict.id || matchedDistrict.district_id;
              cityName = matchedDistrict.name || matchedDistrict.district_name || matchedDistrict.district || cityName;
            }
          }
        } catch (err) {
          console.error('Error loading districts for edit:', err);
        } finally {
          setLoadingDistricts(false);
        }
      }
      
      setFormData({
        agencyId: fullAgencyData.id,
        agencyName: fullAgencyData.agencyName || '',
        agencyType: fullAgencyData.agencyType || '',
        gstNumber: fullAgencyData.gstNumber || '',
        panNumber: fullAgencyData.panNumber || '',
        addressLine1: fullAgencyData.addressLine1 || '',
        addressLine2: fullAgencyData.addressLine2 || '',
        pincode: fullAgencyData.pincode || '',
        country: fullAgencyData.country || 'India',
        state: stateName,
        stateId: stateId,
        city: cityName,
        cityId: cityId,
        latitude: fullAgencyData.latitude || '',
        longitude: fullAgencyData.longitude || '',
        contactPersonName: fullAgencyData.contactPerson || fullAgencyData.contactPersonName || '',
        mobileNumber: fullAgencyData.mobileNumber || '',
        alternateNumber: fullAgencyData.alternateNumber || '',
        emailId: fullAgencyData.emailId || '',
        bankName: fullAgencyData.bankName || '',
        accountHolderName: fullAgencyData.accountHolderName || '',
        accountNumber: fullAgencyData.accountNumber || '',
        ifscCode: fullAgencyData.ifscCode || '',
        upiId: fullAgencyData.upiId || '',
        username: fullAgencyData.username || '',
        password: '', // Security best practice: Always leave password field empty in edit mode
        status: fullAgencyData.status || 'Active'
      });
      setFormErrors({});
      setShowPassword(false);
      setShowEditModal(true);
    } catch (error) {
      console.error('Error in handleEditClick:', error);
      setError('Failed to load agency details for editing.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAgency = async () => {
    if (!validateForm()) return;
    if (!validateLatLong()) return;

    try {
      // Client-side password validation (if provided)
      if (formData.password && (formData.password.length < 6 || formData.password.length > 255)) {
        setError('Password must be between 6 and 255 characters.');
        return;
      }

      // Prepare agency data for API
      const agencyData = mapAgencyToAPI({
        agencyName: formData.agencyName,
        agencyType: formData.agencyType,
        contactPersonName: formData.contactPersonName,
        mobileNumber: formData.mobileNumber,
        alternateNumber: formData.alternateNumber,
        emailId: formData.emailId,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        cityId: formData.cityId,
        state: formData.state,
        stateId: formData.stateId,
        country: formData.country,
        pincode: formData.pincode,
        gstNumber: formData.gstNumber,
        panNumber: formData.panNumber,
        bankName: formData.bankName,
        accountHolderName: formData.accountHolderName,
        accountNumber: formData.accountNumber,
        ifscCode: formData.ifscCode,
        upiId: formData.upiId,
      status: formData.status,
      latitude: formData.latitude,
      longitude: formData.longitude
      });

      // Include user-related fields so backend creates/updates linked users row
      // Backend expects `email_id` (already included via map) and accepts `user_type` and `full_name`
      // full_name prioritizes contact person name, fallback to agency name
      agencyData.user_type = 'Agency';
      agencyData.full_name = formData.contactPersonName || formData.agencyName;
      // ensure we include email in the exact field expected by backend (if mapping didn't transform)
      if (formData.emailId) {
        agencyData.email_id = formData.emailId;
      }

      if (showEditModal && editingAgency) {
        // Include username/password fields only when present
        if (formData.username) agencyData.username = formData.username;
        if (formData.password && formData.password.trim() !== '') {
          agencyData.password = formData.password;
        }

        // Update existing agency via API
        await updateAgency(editingAgency.id, agencyData);
        setSuccessAgencyName(formData.agencyName);
        setSuccessModalType('update');
        // Refresh agency list to reflect changes
        await fetchAgencies();
      } else {
        // Include username/password when creating
        if (formData.username) agencyData.username = formData.username;
        if (formData.password && formData.password.trim() !== '') {
          agencyData.password = formData.password;
        }

        // Create new agency via API
        const response = await createAgency(agencyData);
        const createdAgency = response.data || response;
        setSuccessAgencyName(formData.agencyName);
        setSuccessModalType('add');
        // Refresh agency list
        await fetchAgencies();
      }
      
      handleCloseModal();
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error saving agency:', err);
      setError(err.message || 'Failed to save agency. Please try again.');
    }
  };

  return (
    <div className="agency-management-container">
      <div className="agency-header">
        <div className="filter-group">
          <div className="filter-wrapper" ref={agencyNameDropdownRef}>
            <button
              className={`filter-btn ${agencyNameFilter ? 'active' : ''}`}
              onClick={() => {
                setAgencyNameDropdownOpen(!agencyNameDropdownOpen);
                setCityDropdownOpen(false);
                setStateDropdownOpen(false);
                if (!agencyNameDropdownOpen) {
                  setAgencyNameSearch('');
                }
              }}
            >
              <span>🏢</span> Transporter Name {agencyNameFilter && `(${agencyNameFilter.split(' ')[0]}...)`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {agencyNameDropdownOpen && (
              <div className="filter-dropdown">
                <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                  <input
                    type="text"
                    placeholder="Search transporter name..."
                    value={agencyNameSearch}
                    onChange={(e) => setAgencyNameSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2563EB'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
                <button className={`filter-option ${agencyNameFilter === '' ? 'selected' : ''}`} onClick={() => { setAgencyNameFilter(''); setAgencyNameDropdownOpen(false); setAgencyNameSearch(''); clearPaging(); }}>All</button>
                {filteredAgencyNames.map((name) => (
                  <button key={name} className={`filter-option ${agencyNameFilter === name ? 'selected' : ''}`} onClick={() => { setAgencyNameFilter(name); setAgencyNameDropdownOpen(false); setAgencyNameSearch(''); clearPaging(); }}>{name}</button>
                ))}
                {filteredAgencyNames.length === 0 && (
                  <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>No results found</div>
                )}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={stateDropdownRef}>
            <button
              className={`filter-btn ${stateFilter ? 'active' : ''}`}
              onClick={() => {
                setStateDropdownOpen(!stateDropdownOpen);
                setAgencyNameDropdownOpen(false);
                setCityDropdownOpen(false);
                if (!stateDropdownOpen) {
                  setStateSearch('');
                }
              }}
            >
              <span>📍</span> State {stateFilter && `(${stateFilter})`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {stateDropdownOpen && (
              <div className="filter-dropdown">
                <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                  <input
                    type="text"
                    placeholder="Search state..."
                    value={stateSearch}
                    onChange={(e) => setStateSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2563EB'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
                <button className={`filter-option ${stateFilter === '' ? 'selected' : ''}`} onClick={() => { 
                  setStateFilter(''); 
                  setCityFilter(''); // Clear city when state is cleared
                  setStateDropdownOpen(false); 
                  setStateSearch(''); 
                  clearPaging(); 
                }}>All</button>
                {loadingStates ? (
                  <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>Loading states...</div>
                ) : filteredFilterStates.length === 0 ? (
                  <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>
                    {stateSearch ? 'No states found' : 'No states available'}
                  </div>
                ) : (
                  filteredFilterStates.map((state) => {
                    const stateName = state.name || state.state_name || state.state || '';
                    if (!stateName) return null;
                    return (
                      <button 
                        key={state.id || state.state_id || stateName} 
                        className={`filter-option ${stateFilter === stateName ? 'selected' : ''}`} 
                        onClick={() => { 
                          setStateFilter(stateName); 
                          setCityFilter(''); // Clear city when state changes
                          setStateDropdownOpen(false); 
                          setStateSearch(''); 
                          clearPaging(); 
                        }}
                      >
                        {stateName}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="filter-wrapper" ref={cityDropdownRef}>
            <button
              className={`filter-btn ${cityFilter ? 'active' : ''}`}
              onClick={() => {
                setCityDropdownOpen(!cityDropdownOpen);
                setAgencyNameDropdownOpen(false);
                setStateDropdownOpen(false);
                if (!cityDropdownOpen) {
                  setCitySearch('');
                }
              }}
              disabled={!stateFilter} // Disable if no state selected
            >
              <span>📍</span> City {cityFilter && `(${cityFilter})`}
              <span style={{ marginLeft: 'auto' }}>▼</span>
            </button>
            {cityDropdownOpen && (
              <div className="filter-dropdown">
                <div style={{ padding: '8px', borderBottom: '1px solid #e0e0e0' }}>
                  <input
                    type="text"
                    placeholder={stateFilter ? "Search city/district..." : "Select state first"}
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={!stateFilter}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      opacity: stateFilter ? 1 : 0.6
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#2563EB'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
                {!stateFilter ? (
                  <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>Please select a state first</div>
                ) : (
                  <>
                    <button className={`filter-option ${cityFilter === '' ? 'selected' : ''}`} onClick={() => { setCityFilter(''); setCityDropdownOpen(false); setCitySearch(''); clearPaging(); }}>All</button>
                    {loadingFilterDistricts ? (
                      <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>Loading districts...</div>
                    ) : filteredFilterCities.length === 0 ? (
                      <div style={{ padding: '12px 16px', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>
                        {citySearch ? 'No districts found' : 'No districts available'}
                      </div>
                    ) : (
                      filteredFilterCities.map((district) => {
                        const districtName = district.name || district.district_name || district.district || '';
                        if (!districtName) return null;
                        return (
                          <button 
                            key={district.id || district.district_id || districtName} 
                            className={`filter-option ${cityFilter === districtName ? 'selected' : ''}`} 
                            onClick={() => { 
                              setCityFilter(districtName); 
                              setCityDropdownOpen(false); 
                              setCitySearch(''); 
                              clearPaging(); 
                            }}
                          >
                            {districtName}
                          </button>
                        );
                      })
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="agency-actions">
          <button className="btn-primary" onClick={handleAddAgency}><span>+</span> Add Transporter</button>
          <label className="btn-icon-only" style={{ cursor: 'pointer', margin: 0 }} onClick={handleImportClick} title="Import">
            <span>📥</span>
            <input
              id="agency-import-file-input"
              type="file"
              accept=".csv"
              onChange={handleImportFileChange}
              style={{ display: 'none' }}
            />
          </label>
          <button className="btn-icon-only" onClick={handleExport} title="Export"><span>📤</span></button>
        </div>
      </div>

      <div className="show-entries-container">
        <div className="show-entries-wrapper" style={{ width: '100%' }}>
          <div className="show-entries-left" ref={showEntriesDropdownRef}>
            <label className="show-entries-label">Show</label>
            <button
              className="show-entries-btn"
              onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
            >
              <span>{itemsPerPage}</span>
              <span>▼</span>
            </button>
            {showEntriesDropdownOpen && (
              <div className="show-entries-dropdown">
                {[10, 25, 50, 100].map(num => (
                  <button
                    key={num}
                    className={`show-entries-option ${itemsPerPage === num ? 'selected' : ''}`}
                    onClick={() => {
                      setItemsPerPage(num);
                      setShowEntriesDropdownOpen(false);
                      setCurrentPage(1);
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            )}
            <label className="show-entries-label">entries</label>
          </div>
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, city, or mobile"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container" style={{ padding: '20px', textAlign: 'center' }}>
          <p>Loading transporters...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="error-container" style={{ padding: '20px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px', margin: '10px 0' }}>
          <p>{error}</p>
          <button onClick={fetchAgencies} style={{ marginTop: '10px', padding: '8px 16px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      {/* Table Section */}
      {!loading && (
      <div className="table-wrapper">
        <table className="agency-table">
          <thead>
            <tr>
              <th>Transporter Id</th>
              <th>Transporter Name</th>
              <th>City</th>
              <th>State</th>
              <th>Mobile Number</th>
              <th>Contact Person</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {current.map((agency) => (
              <tr key={agency.id}>
                <td>{agency.id}</td>
                <td>{agency.agencyName}</td>
                <td>{agency.city}</td>
                <td>{agency.state}</td>
                <td>{agency.mobileNumber}</td>
                <td>{agency.contactPerson}</td>
                <td>
                  <span className={`status-badge ${agency.status === 'Active' ? 'active' : 'inactive'}`}>
                    {agency.status}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn view-btn" title="View" onClick={() => handleViewClick(agency)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 3C4.5 3 1.73 5.11 0 8C1.73 10.89 4.5 13 8 13C11.5 13 14.27 10.89 16 8C14.27 5.11 11.5 3 8 3ZM8 11C6.34 11 5 9.66 5 8C5 6.34 6.34 5 8 5C9.66 5 11 6.34 11 8C11 9.66 9.66 11 8 11ZM8 6.5C7.17 6.5 6.5 7.17 6.5 8C6.5 8.83 7.17 9.5 8 9.5C8.83 9.5 9.5 8.83 9.5 8C9.5 7.17 8.83 6.5 8 6.5Z" fill="#2563EB"/>
                      </svg>
                    </button>
                    <button className="action-btn edit-btn" title="Edit" onClick={() => handleEditClick(agency)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.333 3.333L12.667 4.667M10.667 2L14 5.333L5.333 14H2V10.667L10.667 2Z" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    <button className="action-btn delete-btn" title="Delete" onClick={() => handleDeleteClick(agency)}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="4" y="5.5" width="8" height="8.5" rx="0.5" stroke="#dc2626" strokeWidth="1.25" fill="none"/>
                        <path d="M2 5.5h12M6 5.5V4.25C6 3.83579 6.33579 3.5 6.75 3.5h2.5C9.66421 3.5 10 3.83579 10 4.25V5.5" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                        <line x1="6.5" y1="8.5" x2="6.5" y2="12" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                        <line x1="9.5" y1="8.5" x2="9.5" y2="12" stroke="#dc2626" strokeWidth="1.25" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination Section */}
      {!loading && (
      <div className="pagination-container">
        <div className="pagination-info">
          Showing {displayTotal > 0 ? pageStart + 1 : 0} to {Math.min(pageStart + itemsPerPage, displayTotal)} of {displayTotal} entries
        </div>
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
      </div>
      )}

      {/* Add/Edit Agency Modal */}
      {(showAddModal || showEditModal) && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <div>{showEditModal ? 'Edit Transporter' : 'Add Transporter'}</div>
              <button className="action-btn" onClick={handleCloseModal}>✖</button>
            </div>
            <div className="modal-body" style={{ paddingBottom: '32px' }} autoComplete="off">
              {/* Agency Details Section */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '16px' }}>Transporter Details</h3>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-field">
                  <label>Transporter Id</label>
                  <input 
                    className="form-input" 
                    value={formData.agencyId} 
                    readOnly
                    placeholder="Auto-generated"
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>
                    Transporter Name <span className="required-asterisk">*</span>
                  </label>
                  <input 
                    className={`form-input ${formErrors.agencyName ? 'error' : ''}`}
                    placeholder="Enter transporter name" 
                    value={formData.agencyName}
                    onChange={(e) => {
                      setFormData({ ...formData, agencyName: e.target.value });
                      if (formErrors.agencyName) setFormErrors({...formErrors, agencyName: ''});
                    }}
                  />
                  {formErrors.agencyName && <div className="error-message">{formErrors.agencyName}</div>}
                </div>
                <div className="form-field">
                  <label>Transporter Type</label>
                  <select 
                    className="form-input" 
                    value={formData.agencyType}
                    onChange={(e) => setFormData({ ...formData, agencyType: e.target.value })}
                  >
                    <option value="">Select Transporter Type</option>
                    <option value="Delivery">Delivery</option>
                    <option value="Logistics">Logistics</option>
                    <option value="Transport">Transport</option>
                    <option value="Freight">Freight</option>
                    <option value="Express">Express</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>GST Number</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter GST number" 
                    value={formData.gstNumber}
                    onChange={(e) => {
                      setFormData({ ...formData, gstNumber: e.target.value });
                      if (formErrors.gstNumber) setFormErrors({ ...formErrors, gstNumber: '' });
                    }}
                  />
                  {formErrors.gstNumber && <div className="error-message">{formErrors.gstNumber}</div>}
                </div>
                <div className="form-field">
                  <label>PAN Number</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter PAN number" 
                    value={formData.panNumber}
                    onChange={(e) => {
                      setFormData({ ...formData, panNumber: e.target.value });
                      if (formErrors.panNumber) setFormErrors({ ...formErrors, panNumber: '' });
                    }}
                  />
                  {formErrors.panNumber && <div className="error-message">{formErrors.panNumber}</div>}
                </div>
              </div>

              {/* Address & Contact Information Section */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginTop: '24px', marginBottom: '16px' }}>Address & Contact Information</h3>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="form-field">
                  <label>Address Line 1</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter address line 1" 
                    value={formData.addressLine1}
                    onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Address Line 2 (optional)</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter address line 2" 
                    value={formData.addressLine2}
                    onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Pincode</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter pincode" 
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Country</label>
                  <select 
                    className="form-input" 
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  >
                    <option value="">Select Country</option>
                    <option value="India">India</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>State</label>
                  <div className="searchable-select-wrapper" ref={formStateDropdownRef} style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={loadingStates ? 'Loading states...' : 'Search and select state'}
                      value={formStateDropdownOpen ? formStateSearch : (formData.state || '')}
                      onFocus={() => {
                        setFormStateDropdownOpen(true);
                        setFormStateSearch('');
                      }}
                      onChange={(e) => {
                        setFormStateSearch(e.target.value);
                        setFormStateDropdownOpen(true);
                      }}
                      onClick={() => setFormStateDropdownOpen(true)}
                      disabled={loadingStates}
                    />
                    {formStateDropdownOpen && (
                      <div className="searchable-dropdown" style={{
                        position: 'absolute',
                        zIndex: 1000,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        width: '100%',
                        maxHeight: '200px',
                        overflow: 'auto',
                        borderRadius: '6px',
                        marginTop: '4px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}>
                        {loadingStates ? (
                          <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>Loading states...</div>
                        ) : filteredFormStates.length === 0 ? (
                          <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>
                            {formStateSearch ? 'No states found' : 'No states available'}
                          </div>
                        ) : (
                          filteredFormStates.map(state => {
                            const stateName = state.name || state.state_name || state.state || '';
                            if (!stateName) return null;
                            return (
                              <div
                                key={state.id || state.state_id || stateName}
                                onClick={() => {
                                  const stateId = state.id || state.state_id;
                                  setFormData({ 
                                    ...formData, 
                                    state: stateName, 
                                    stateId: stateId,
                                    city: '',
                                    cityId: null
                                  });
                                  setDistricts([]);
                                  setFormStateDropdownOpen(false);
                                  setFormStateSearch('');
                                }}
                                style={{
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f3f4f6',
                                  backgroundColor: formData.state === stateName ? '#f3f4f6' : '#fff'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = formData.state === stateName ? '#f3f4f6' : '#fff'}
                              >
                                {stateName}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-field">
                  <label>City</label>
                  <div className="searchable-select-wrapper" ref={formCityDropdownRef} style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={!formData.state ? 'Select State first' : (loadingDistricts ? 'Loading districts...' : 'Search and select city')}
                      value={formCityDropdownOpen ? formCitySearch : (formData.city || '')}
                      onFocus={() => {
                        if (formData.state && !loadingDistricts) {
                          setFormCityDropdownOpen(true);
                          setFormCitySearch('');
                        }
                      }}
                      onChange={(e) => {
                        setFormCitySearch(e.target.value);
                        setFormCityDropdownOpen(true);
                      }}
                      onClick={() => {
                        if (formData.state && !loadingDistricts) {
                          setFormCityDropdownOpen(true);
                        }
                      }}
                      disabled={!formData.state || loadingDistricts}
                    />
                    {formCityDropdownOpen && formData.state && (
                      <div className="searchable-dropdown" style={{
                        position: 'absolute',
                        zIndex: 1000,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        width: '100%',
                        maxHeight: '200px',
                        overflow: 'auto',
                        borderRadius: '6px',
                        marginTop: '4px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}>
                        {loadingDistricts ? (
                          <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>Loading districts...</div>
                        ) : filteredFormCities.length === 0 ? (
                          <div style={{ padding: '12px', color: '#6b7280', textAlign: 'center' }}>
                            {formCitySearch ? 'No cities found' : 'No districts available'}
                          </div>
                        ) : (
                          filteredFormCities.map(city => {
                            // Find district ID from districts array
                            const district = districts.find(d => {
                              const districtName = d.name || d.district_name || d.district || '';
                              return districtName === city;
                            });
                            const districtId = district ? (district.id || district.district_id) : null;
                            
                            return (
                              <div
                                key={districtId || city}
                                onClick={() => {
                                  setFormData({ 
                                    ...formData, 
                                    city: city,
                                    cityId: districtId
                                  });
                                  setFormCityDropdownOpen(false);
                                  setFormCitySearch('');
                                }}
                                style={{
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f3f4f6',
                                  backgroundColor: formData.city === city ? '#f3f4f6' : '#fff'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = formData.city === city ? '#f3f4f6' : '#fff'}
                              >
                                {city}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-field">
                  <label>Latitude</label>
                  <input 
                    className={`form-input ${formErrors.latitude ? 'error' : ''}`}
                    type="number"
                    step="any"
                    placeholder="Enter latitude (e.g., 19.0760)" 
                    value={formData.latitude}
                    onChange={(e) => {
                      setFormData({ ...formData, latitude: e.target.value });
                      if (formErrors.latitude) setFormErrors({...formErrors, latitude: ''});
                    }}
                  />
                  {formErrors.latitude && <div className="error-message">{formErrors.latitude}</div>}
                </div>
                <div className="form-field">
                  <label>Longitude</label>
                  <input 
                    className={`form-input ${formErrors.longitude ? 'error' : ''}`}
                    type="number"
                    step="any"
                    placeholder="Enter longitude (e.g., 72.8777)" 
                    value={formData.longitude}
                    onChange={(e) => {
                      setFormData({ ...formData, longitude: e.target.value });
                      if (formErrors.longitude) setFormErrors({...formErrors, longitude: ''});
                    }}
                  />
                  {formErrors.longitude && <div className="error-message">{formErrors.longitude}</div>}
                </div>
                <div className="form-field">
                  <label>Contact Person Name</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter contact person name" 
                    value={formData.contactPersonName}
                    onChange={(e) => setFormData({ ...formData, contactPersonName: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>
                    Mobile Number <span className="required-asterisk">*</span>
                  </label>
                  <div className="input-with-suffix">
                    <input 
                      className={`form-input ${formErrors.mobileNumber ? 'error' : ''}`}
                      type="tel"
                      placeholder="Enter mobile number" 
                      value={formData.mobileNumber}
                      onChange={(e) => {
                        setFormData({ ...formData, mobileNumber: e.target.value });
                        if (formErrors.mobileNumber) setFormErrors({...formErrors, mobileNumber: ''});
                      }}
                      maxLength="10"
                    />
                    <span className="input-suffix">📱</span>
                  </div>
                  {formErrors.mobileNumber && <div className="error-message">{formErrors.mobileNumber}</div>}
                </div>
                <div className="form-field">
                  <label>Alternate Number (optional)</label>
                  <div className="input-with-suffix">
                    <input 
                      className="form-input"
                      type="tel"
                      placeholder="Enter alternate number" 
                      value={formData.alternateNumber}
                      onChange={(e) => setFormData({ ...formData, alternateNumber: e.target.value })}
                      maxLength="10"
                    />
                    <span className="input-suffix">☎</span>
                  </div>
                </div>
                <div className="form-field">
                  <label>Email ID</label>
                  <div className="input-with-suffix">
                    <input 
                      className={`form-input ${formErrors.emailId ? 'error' : ''}`}
                      type="email"
                      placeholder="Enter email address" 
                      value={formData.emailId || ''}
                      autoComplete="off"
                      onChange={(e) => {
                        const emailValue = e.target.value;
                        setFormData({ ...formData, emailId: emailValue });
                        
                        // Clear error when user starts typing
                        if (formErrors.emailId) {
                          setFormErrors({...formErrors, emailId: ''});
                        }
                        
                        // Real-time validation for email uniqueness (debounced)
                        if (emailValue && emailValue.trim()) {
                          const emailToCheck = emailValue.trim().toLowerCase();
                          // Only check if email format is valid first
                          if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToCheck)) {
                            const existingAgency = allAgenciesForFilters.find(agency => {
                              // Exclude current agency when editing
                              if (showEditModal && editingAgency && (agency.id === editingAgency.id || agency.id === formData.agencyId)) {
                                return false;
                              }
                              const agencyEmail = (agency.emailId || '').toLowerCase();
                              return agencyEmail === emailToCheck && agencyEmail !== '';
                            });
                            
                            if (existingAgency) {
                              setFormErrors({...formErrors, emailId: 'This email address is already in use by another transporter'});
                            }
                          }
                        }
                      }}
                    />
                    <span className="input-suffix">✉</span>
                  </div>
                  {formErrors.emailId && <div className="error-message">{formErrors.emailId}</div>}
                </div>
                <div className="form-field">
                  <label>Password {showEditModal ? <span style={{fontSize:'0.85rem', color:'#666'}}> (leave blank to keep)</span> : null}</label>
                  <div className="input-with-suffix">
                    {/* Hidden dummy password field to prevent browser autofill - placed before real field */}
                    <input
                      type="password"
                      name="password-hidden"
                      style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none', zIndex: -1 }}
                      tabIndex={-1}
                      autoComplete="new-password"
                      readOnly
                      value=""
                    />
                    <input 
                      className="form-input"
                      type={showPassword ? 'text' : 'password'}
                      name={showEditModal ? "transporter-password-edit" : "transporter-password-new"}
                      placeholder={showEditModal ? "Enter password to change" : "Enter password"} 
                      value={formData.password || ''}
                      autoComplete="off"
                      data-form-type="other"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                    <span 
                      className="input-suffix" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </span>
                  </div>
                  {formErrors.password && <div className="error-message">{formErrors.password}</div>}
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={formData.status === 'Active'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'Active' : 'Inactive' })}
                        style={{ display: 'none' }}
                      />
                      <div style={{
                        width: '50px',
                        height: '26px',
                        borderRadius: '13px',
                        backgroundColor: formData.status === 'Active' ? '#2563EB' : '#ccc',
                        position: 'relative',
                        transition: 'background-color 0.3s',
                        cursor: 'pointer'
                      }}>
                        <div style={{
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          position: 'absolute',
                          top: '2px',
                          left: formData.status === 'Active' ? '26px' : '2px',
                          transition: 'left 0.3s',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                      <span style={{ marginLeft: '8px', fontSize: '0.9rem', color: '#333' }}>Active</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Bank Details (Optional) Section */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginTop: '24px', marginBottom: '16px' }}>Bank Details (Optional)</h3>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '16px' }}>
                <div className="form-field">
                  <label>Bank Name</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter bank name" 
                    value={formData.bankName}
                    onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Account Holder Name</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter account holder name" 
                    value={formData.accountHolderName}
                    onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Account Number</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter account number" 
                    value={formData.accountNumber}
                    onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>IFSC Code</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter IFSC code" 
                    value={formData.ifscCode}
                    onChange={(e) => {
                      setFormData({ ...formData, ifscCode: e.target.value });
                      if (formErrors.ifscCode) setFormErrors({ ...formErrors, ifscCode: '' });
                    }}
                  />
                  {formErrors.ifscCode && <div className="error-message">{formErrors.ifscCode}</div>}
                </div>
                <div className="form-field">
                  <label>UPI Id</label>
                  <input 
                    className="form-input" 
                    placeholder="Enter UPI ID" 
                    value={formData.upiId}
                    onChange={(e) => {
                      setFormData({ ...formData, upiId: e.target.value });
                      if (formErrors.upiId) setFormErrors({ ...formErrors, upiId: '' });
                    }}
                  />
                  {formErrors.upiId && <div className="error-message">{formErrors.upiId}</div>}
                </div>
              </div>

              
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseModal}>Cancel</button>
              <button className="btn-secondary" onClick={handleReset}>Reset</button>
              <button className="btn-primary" onClick={handleSaveAgency}>{showEditModal ? 'Update' : 'Add'}</button>
            </div>
          </div>
        </div>
      )}

      {/* View Agency Modal */}
      {showViewModal && viewingAgency && (
        <div className="modal-overlay" onClick={() => { setShowViewModal(false); setViewingAgency(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div>View Transporter Details</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>{viewingAgency.agencyName}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span className={`status-badge ${viewingAgency.status === 'Active' ? 'active' : 'inactive'}`}>
                  {viewingAgency.status}
                </span>
                <button className="action-btn" onClick={() => { setShowViewModal(false); setViewingAgency(null); }}>✖</button>
              </div>
            </div>
            <div className="modal-body" style={{ paddingBottom: '32px' }}>
              {/* Agency Details Section */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '16px' }}>Transporter Details</h3>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                <div className="form-field">
                  <label>Transporter Code</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.id || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Transporter Name</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.agencyName || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Transporter Type</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.agencyType || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>GST Number</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.gstNumber || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>PAN Number</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.panNumber || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Address & Contact Information Section */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginTop: '24px', marginBottom: '16px' }}>Address & Contact Information</h3>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                <div className="form-field">
                  <label>Address Line 1</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.addressLine1 || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Address Line 2</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.addressLine2 || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>City</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.city || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Pincode</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.pincode || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>State</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.state || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Country</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.country || 'India'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Contact Person</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.contactPerson || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Mobile Number</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>📱</span>
                    {viewingAgency.mobileNumber || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Alternate Number</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>☎</span>
                    {viewingAgency.alternateNumber || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Email ID</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>✉</span>
                    {viewingAgency.emailId || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Bank Details Section */}
              <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginTop: '24px', marginBottom: '16px' }}>Bank Details</h3>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '16px' }}>
                <div className="form-field">
                  <label>Bank Name</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.bankName || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Account Name</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.accountHolderName || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>Account Number</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.accountNumber || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>IFSC Code</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.ifscCode || 'N/A'}
                  </div>
                </div>
                <div className="form-field">
                  <label>UPI Id</label>
                  <div style={{ padding: '10px 12px', fontSize: '0.95rem', color: '#333', backgroundColor: '#f9f9f9', borderRadius: '4px', minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                    {viewingAgency.upiId || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => { setShowViewModal(false); setViewingAgency(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Agency Confirmation Modal */}
      {showDeleteConfirm && agencyToDelete && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-vehicle-header">
              <div className="delete-warning-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="delete-vehicle-title">Confirm Action</h3>
            </div>
            <div className="delete-vehicle-body">
              <p className="delete-vehicle-message">
                Are you sure you want to delete this Transporter? This action cannot be undone.
              </p>
              <div className="delete-vehicle-details">
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Transporter Name:</span>
                  <span className="delete-detail-value">{agencyToDelete.agencyName}</span>
                </div>
              </div>
            </div>
            <div className="delete-vehicle-footer">
              <button className="delete-vehicle-cancel-btn" onClick={handleDeleteCancel}>
                Cancel
              </button>
              <button className="delete-vehicle-delete-btn" onClick={handleDeleteConfirm}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
                  <path d="M6.5 4V3.5C6.5 2.94772 6.94772 2.5 7.5 2.5H8.5C9.05228 2.5 9.5 2.94772 9.5 3.5V4M12 4H4M11 4V13.5C11 14.0523 10.5523 14.5 10 14.5H6C5.44772 14.5 5 14.0523 5 13.5V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6.5 7V11.5M9.5 7V11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Agency Success Modal */}
      {showDeleteSuccess && deletedAgencyInfo && (
        <div className="success-modal-overlay" onClick={handleDeleteSuccessClose}>
          <div className="delete-success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-success-header">
              <div className="delete-success-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="delete-success-title">Success</h3>
              <button className="delete-success-close" onClick={handleDeleteSuccessClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', color: '#999', cursor: 'pointer', padding: '0', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✖</button>
            </div>
            <div className="delete-success-body">
              <p className="delete-success-message">Transporter successfully deleted</p>
              <div className="delete-success-details">
                <div className="delete-success-detail-row">
                  <span className="delete-success-detail-label">Transporter Name:</span>
                  <span className="delete-success-detail-value">{deletedAgencyInfo.agencyName}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sample CSV Modal */}
      {showSampleCSVModal && (
        <div className="modal-overlay" onClick={() => setShowSampleCSVModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
            <div className="modal-header">
              <div>Import Transporters</div>
              <button className="action-btn" onClick={() => setShowSampleCSVModal(false)}>✖</button>
            </div>
            <div className="modal-body">
              <div className="form-field">
                <label>Sample CSV Format</label>
                <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '16px' }}>
                  Download the sample CSV template to see the required format for importing transporters.
                  <br />
                  <span style={{ fontSize: '0.85rem', color: '#999', fontStyle: 'italic' }}>
                    Note: Transporter ID will be auto-generated if not provided.
                  </span>
                </p>
                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button 
                    className="btn-primary" 
                    onClick={handleDownloadSampleCSV}
                    style={{ flex: 1 }}
                  >
                    <span>⬇</span> Download Sample CSV
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowSampleCSVModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleProceedWithImport}>
                Proceed with Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={handleImportCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '90%' }}>
            <div className="modal-header">
              <div>Import Transporters</div>
              <button className="action-btn" onClick={handleImportCancel}>✖</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#666' }}>File: <strong>{importFileName}</strong></p>
                <p style={{ margin: '0', fontSize: '0.85rem', color: '#999' }}>
                  {importValidAgencies.length} valid transporter{importValidAgencies.length !== 1 ? 's' : ''} found.
                  {importInvalidRows.length > 0 && ` ${importInvalidRows.length} row${importInvalidRows.length !== 1 ? 's' : ''} will be skipped.`}
                </p>
              </div>

              {importInvalidRows.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#dc2626', margin: '0 0 12px 0' }}>Validation Summary</h4>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '8px' }}>
                    {importInvalidRows.map((item, idx) => (
                      <div key={idx} style={{ padding: '8px', fontSize: '0.85rem', borderBottom: idx < importInvalidRows.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                        <span style={{ color: '#666' }}>Row {item.row}:</span> <strong>{item.agencyName}</strong> - <span style={{ color: '#dc2626' }}>{item.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importValidAgencies.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#333', margin: '0 0 12px 0' }}>Agencies to Import</h4>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead style={{ background: '#f8f9fa', position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>ID</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Name</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Mobile</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importValidAgencies.slice(0, 10).map((a, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '8px' }}>{a.id}</td>
                            <td style={{ padding: '8px' }}>{a.agencyName}</td>
                            <td style={{ padding: '8px' }}>{a.mobileNumber}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {importValidAgencies.length > 10 && (
                      <div style={{ padding: '8px', textAlign: 'center', fontSize: '0.85rem', color: '#666' }}>
                        ... and {importValidAgencies.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleImportCancel}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleImportConfirm}
                disabled={importValidAgencies.length === 0}
                style={{ opacity: importValidAgencies.length === 0 ? 0.5 : 1, cursor: importValidAgencies.length === 0 ? 'not-allowed' : 'pointer' }}
              >
                Import {importValidAgencies.length > 0 && `(${importValidAgencies.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Success Modal */}
      {showImportSuccess && (
        <div className="success-modal-overlay" onClick={() => setShowImportSuccess(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Import Successful!</h2>
            <p className="success-message">
              {importSuccessCount} transporter{importSuccessCount !== 1 ? 's' : ''} imported successfully.
              {importSkippedCount > 0 && ` ${importSkippedCount} row${importSkippedCount !== 1 ? 's' : ''} skipped.`}
            </p>
            <button className="success-ok-btn" onClick={() => setShowImportSuccess(false)}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Success!</h2>
            <p className="success-message">Agency details have been successfully {successModalType === 'add' ? 'Added' : 'Updated'}</p>
            {successAgencyName && (
              <div className="success-id-badge">{successAgencyName}</div>
            )}
            <button className="success-ok-btn" onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyManagement;
